import { useState } from "react";
import { ChevronDown, ChevronRight, Edit2, Trash2, Plus } from "lucide-react";
import { Button } from "../../ui/button";

export const LeagueStructureSection = ({
  league,
  onAddConference,
  onEditConference,
  onDeleteConference,
  onAddDivision,
  onEditDivision,
  onDeleteDivision
}) => {
  const [expandedConfs, setExpandedConfs] = useState({});

  const toggleConf = (confId) => {
    setExpandedConfs(prev => ({ ...prev, [confId]: !prev[confId] }));
  };

  const getDivisionsForConference = (confId) => {
    return league.divisions?.filter(d => d.conference_id === confId) || [];
  };

  const getOrphanDivisions = () => {
    return league.divisions?.filter(d => !d.conference_id) || [];
  };

  return (
    <div className="space-y-6">
      {/* Conferences */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-bold text-zinc-400 uppercase">Conferences</h4>
          <Button size="sm" variant="outline" onClick={onAddConference}>
            <Plus className="w-4 h-4 mr-1" /> Add Conference
          </Button>
        </div>
        {league.conferences?.length === 0 ? (
          <p className="text-zinc-500 text-sm">No conferences. Add conferences or use divisions directly.</p>
        ) : (
          <div className="space-y-2">
            {league.conferences?.map(conf => (
              <div key={conf.id} className="bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={() => toggleConf(conf.id)}
                >
                  <div className="flex items-center gap-3">
                    {expandedConfs[conf.id] ? (
                      <ChevronDown className="w-4 h-4 text-zinc-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-zinc-500" />
                    )}
                    {conf.logo_url ? (
                      <img src={conf.logo_url} alt="" className="w-8 h-8 object-contain" />
                    ) : (
                      <div
                        className="w-8 h-8 rounded flex items-center justify-center"
                        style={{ backgroundColor: conf.color || league.color || "#f97316" }}
                      >
                        <span className="text-xs font-bold text-white">{conf.abbreviation || conf.name.charAt(0)}</span>
                      </div>
                    )}
                    <span className="text-white font-medium">{conf.name}</span>
                    <span className="text-xs text-zinc-500">
                      ({getDivisionsForConference(conf.id).length} divisions)
                    </span>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => onEditConference(conf)}
                      className="p-1 text-zinc-400 hover:text-white"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDeleteConference(conf.id)}
                      className="p-1 text-zinc-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {expandedConfs[conf.id] && (
                  <div className="border-t border-zinc-700 p-4 space-y-2">
                    {getDivisionsForConference(conf.id).map(div => (
                      <div
                        key={div.id}
                        className="flex items-center justify-between py-2 px-3 bg-zinc-900 rounded"
                      >
                        <div className="flex items-center gap-3">
                          {div.logo_url ? (
                            <img src={div.logo_url} alt="" className="w-6 h-6 object-contain" />
                          ) : (
                            <div
                              className="w-6 h-6 rounded flex items-center justify-center"
                              style={{ backgroundColor: div.color || conf.color || "#666" }}
                            >
                              <span className="text-[10px] font-bold text-white">{div.abbreviation || div.name.charAt(0)}</span>
                            </div>
                          )}
                          <span className="text-zinc-300">{div.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onEditDivision(div)}
                            className="p-1 text-zinc-400 hover:text-white"
                          >
                            <Edit2 className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => onDeleteDivision(div.id)}
                            className="p-1 text-zinc-400 hover:text-red-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Divisions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-bold text-zinc-400 uppercase">
            Divisions {league.conferences?.length > 0 ? "(Unassigned)" : ""}
          </h4>
          <Button size="sm" variant="outline" onClick={onAddDivision}>
            <Plus className="w-4 h-4 mr-1" /> Add Division
          </Button>
        </div>
        {getOrphanDivisions().length === 0 ? (
          <p className="text-zinc-500 text-sm">
            {league.conferences?.length > 0
              ? "All divisions are assigned to conferences."
              : "No divisions yet. Add divisions to organize teams."}
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {getOrphanDivisions().map(div => (
              <div
                key={div.id}
                className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700"
              >
                <div className="flex items-center gap-3">
                  {div.logo_url ? (
                    <img src={div.logo_url} alt="" className="w-8 h-8 object-contain" />
                  ) : (
                    <div
                      className="w-8 h-8 rounded flex items-center justify-center"
                      style={{ backgroundColor: div.color || league.color || "#f97316" }}
                    >
                      <span className="text-xs font-bold text-white">{div.abbreviation || div.name.charAt(0)}</span>
                    </div>
                  )}
                  <span className="text-white">{div.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onEditDivision(div)}
                    className="p-1 text-zinc-400 hover:text-white"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDeleteDivision(div.id)}
                    className="p-1 text-zinc-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
